import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { activitiesQueryOptions, buildActivityFilters } from "../api/activities-query";
import { countByRootCategory } from "../lib/category-rank";

/**
 * Bookable places per top-level category in one city, for ordering a city
 * page's category tabs by stock (owner, 2026-10-06).
 *
 * Read from the city overview's own listing (the 200-row page /place/<city>
 * loads), never fetched for this: `enabled: false` only subscribes to the
 * cache. On the overview the counts are there from the first render, on the
 * server too, so the tabs never jump. On a city + category page they are
 * there when the guest came from the overview, which keeps the tabs in the
 * order they just saw; landing there directly, there are no counts and the
 * fixed ranking applies.
 */
export function useCityCategoryCounts(
  lang: string,
  citySlug: string | undefined,
  /** The city's category rows, for rolling a subcategory up to its parent. */
  categoryRows: readonly { slug: string; parentSlug?: string | null }[] | undefined,
): Map<string, number> | null {
  const options = activitiesQueryOptions(buildActivityFilters({ lang, city: citySlug }, {}));
  const { data } = useQuery({ ...options, enabled: false });
  const items = citySlug
    ? ((data as { data?: { category?: { slug?: string | null } | null }[] } | undefined)?.data ?? null)
    : null;
  return useMemo(() => {
    if (!items) return null;
    const parents = new Map((categoryRows ?? []).map((c) => [c.slug, c.parentSlug ?? null]));
    return countByRootCategory(items, (slug) => parents.get(slug));
  }, [items, categoryRows]);
}
